import React, { useState } from "react";
import { api, setAuthToken, storeAuthTokens } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Shared email/password auth form. mode: "login" | "register" | "forgot".
export const AuthForm = ({ initialMode = "login", onSuccess }) => {
  const { checkAuth } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const reset = (m) => { setMode(m); setError(""); setNotice(""); setPassword(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "login") {
        const res = await api.post("/auth/login", { email, password });
        if (res?.data?.session_token) setAuthToken(res.data.session_token);
        storeAuthTokens(res?.data);   // also keeps the persistent "stay signed in" credential
        const me = await checkAuth();
        toast.success("Welcome back");
        onSuccess && onSuccess(me);
      } else if (mode === "register") {
        const res = await api.post("/auth/register", { username, email, password, confirm_password: confirm });
        // Switch to sign-in but KEEP the confirmation notice visible (don't call reset() — it clears notice).
        setMode("login"); setError(""); setPassword(""); setConfirm("");
        // Show the server's message — it differs for a brand-new account vs claiming an existing one.
        setNotice(res?.data?.message || "Account created. Check your email for a verification link, then sign in.");
      } else if (mode === "forgot") {
        const res = await api.post("/auth/forgot-password", { email });
        setNotice(res?.data?.message || "If that account exists, a reset link was sent.");
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      if (status === 403 && /verify/i.test(msg)) {
        setNotice("Need a new link? Resend it below.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email }); toast.success("Verification link sent (check the email)."); }
    catch (e) { toast.error("Could not resend right now."); }
  };

  const titles = { login: "Sign in", register: "Create your account", forgot: "Reset your password" };
  const subtitles = {
    login: "Welcome back to BareFootage",
    register: "Join free to rate, comment and follow",
    forgot: "We'll email you a link to set a new password",
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
      <div className="space-y-1">
        <h2 className="font-heading text-2xl text-primary">{titles[mode]}</h2>
        <p className="text-sm text-muted-foreground">{subtitles[mode]}</p>
      </div>

      {mode === "register" && (
        <div className="space-y-1.5">
          <Label htmlFor="af-username">Display name</Label>
          <Input id="af-username" data-testid="register-name-input" value={username}
            onChange={(e) => setUsername(e.target.value)} placeholder="e.g. SandyToes" required
            autoComplete="username" />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="af-email">Email</Label>
        <Input id="af-email" type="email"
          data-testid={mode === "register" ? "register-email-input" : "login-email-input"}
          value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required
          autoComplete="email" />
      </div>

      {mode !== "forgot" && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="af-password">Password</Label>
            {mode === "login" && (
              <button type="button" data-testid="login-forgot-password-link"
                onClick={() => reset("forgot")} className="text-xs text-primary hover:underline">
                Forgot password?
              </button>
            )}
          </div>
          <Input id="af-password" type="password"
            data-testid={mode === "register" ? "register-password-input" : "login-password-input"}
            value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
            required minLength={8} autoComplete={mode === "register" ? "new-password" : "current-password"} />
        </div>
      )}

      {mode === "register" && (
        <div className="space-y-1.5">
          <Label htmlFor="af-confirm">Confirm password</Label>
          <Input id="af-confirm" type="password" data-testid="register-password-confirm-input"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your password"
            required minLength={8} autoComplete="new-password" />
        </div>
      )}

      {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}
      {notice && <p data-testid="auth-notice" className="text-sm text-primary">{notice}</p>}

      {error && /verify/i.test(error) && (
        <Button type="button" variant="outline" onClick={resend} data-testid="auth-resend-btn"
          className="w-full rounded-full">
          <Mail className="w-4 h-4 mr-2" /> Resend verification email
        </Button>
      )}

      <Button type="submit" disabled={busy} data-testid={mode === "register" ? "register-submit-button" : (mode === "forgot" ? "forgot-submit-button" : "login-submit-button")}
        className="w-full rounded-full font-medium">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
      </Button>

      <div className="text-center text-sm text-muted-foreground">
        {mode === "login" && (
          <span>New here?{" "}
            <button type="button" data-testid="login-register-link" onClick={() => reset("register")}
              className="text-primary hover:underline font-medium">Create an account</button>
          </span>
        )}
        {mode === "register" && (
          <span>Already have an account?{" "}
            <button type="button" data-testid="register-login-link" onClick={() => reset("login")}
              className="text-primary hover:underline font-medium">Sign in</button>
          </span>
        )}
        {mode === "forgot" && (
          <button type="button" data-testid="forgot-back-link" onClick={() => reset("login")}
            className="text-primary hover:underline font-medium">Back to sign in</button>
        )}
      </div>
    </form>
  );
};
